/* ==========================================================================
   Address API Functions
   Address autocomplete and verification endpoints
   Task F38: Address autocomplete component + integration
   ========================================================================== */

import type { AxiosInstance } from 'axios'
import { apiClient } from './client'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export interface AddressSuggestion {
  street: string
  city: string
  state: string
  zipcode: string
  full_text: string
}

export interface AutocompleteResponse {
  suggestions: AddressSuggestion[]
}

export interface AddressVerifyRequest {
  street: string
  city: string
  state: string
  zipcode: string
}

export interface AddressVerifyResponse {
  verified: boolean
  standardized_address: AddressSuggestion | null
  original_address: Record<string, string>
  suggestions: AddressSuggestion[]
}

/* --------------------------------------------------------------------------
   API Functions
   -------------------------------------------------------------------------- */

/**
 * Get address autocomplete suggestions.
 * @param query - Partial address string (min 2 chars)
 * @param client - Axios instance (default: provider apiClient, pass adminClient for admin context)
 */
export async function autocompleteAddress(
  query: string,
  client: AxiosInstance = apiClient
): Promise<AddressSuggestion[]> {
  const response = await client.get<AutocompleteResponse>(
    '/address/autocomplete',
    { params: { q: query } }
  )
  return response.data.suggestions
}

/**
 * Verify an address against USPS records.
 * @param data - Address fields to verify
 * @param client - Axios instance (default: provider apiClient, pass adminClient for admin context)
 */
export async function verifyAddress(
  data: AddressVerifyRequest,
  client: AxiosInstance = apiClient
): Promise<AddressVerifyResponse> {
  const response = await client.post<AddressVerifyResponse>(
    '/address/verify',
    data
  )
  return response.data
}
